import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import withStyles from '@material-ui/core/styles/withStyles';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import messages from 'aurora-translations';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { FILTERS_DATA, FIELD_TYPES, SEARCH_FIELD_KEY, DEPENDENT_FILTERS } from '../constants';
import RemovableFiltersTags from './RemovableFiltersTags';

import styles from './styles';

const FiltersTopBar = ({
  intl,
  classes,
  totalResults,
  appliedFilters,
  dropDowns,
  searchValue,
  removeFilterHandler,
}) => {
  const getLabelFromDropDowns = (ddKey, value) => {
    if (isArray(dropDowns[ddKey])) {
      const selectOption = dropDowns[ddKey]?.find(opt => opt?.value === value);
      return !isEmpty(selectOption) ? selectOption?.label : '';
    }
    return '';
  };

  const appliedFiltersList = useMemo(() => {
    return Object.keys(appliedFilters)?.reduce(
      (result, currentKey) => {
        const currentFilter = FILTERS_DATA[currentKey];
        const value =
          Array.isArray(appliedFilters[currentKey]) || Number.isNaN(+appliedFilters[currentKey])
            ? appliedFilters[currentKey]
            : +appliedFilters[currentKey];
        const name = isObject(messages[FILTERS_DATA[currentKey]?.label])
          ? intl.formatMessage({ ...messages[FILTERS_DATA[currentKey]?.label] })
          : '';
        let label = '';
        switch (currentFilter?.type) {
          case FIELD_TYPES?.FIELD:
            label = value;
            break;
          case FIELD_TYPES?.DATE:
            label = moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '';
            break;
          case FIELD_TYPES?.RADIO:
            if (isArray(value)) {
              label = value?.map(valueItem => getLabelFromDropDowns(currentFilter?.dropDownName, valueItem));
            } else {
              label = getLabelFromDropDowns(currentFilter?.dropDownName, value);
            }
            break;
          case FIELD_TYPES?.SELECT:
            if (isArray(value)) {
              label = value?.map(valueItem => getLabelFromDropDowns(currentFilter?.dropDownName, valueItem));
            } else {
              label = getLabelFromDropDowns(currentFilter?.dropDownName, value);
            }
            break;
          default:
            break;
        }
        return [
          ...result,
          {
            key: currentKey,
            name,
            value,
            label,
          },
        ];
      },
      [...(searchValue ? [{ key: SEARCH_FIELD_KEY, name: 'search', value: searchValue, label: searchValue }] : [])],
    );
  }, [appliedFilters, dropDowns]);

  const handleRemoveFilter = (key, value) => {
    let dependentFilterName = DEPENDENT_FILTERS[key];

    const dependentFiltersList = [];
    while (dependentFilterName) {
      dependentFiltersList.push(dependentFilterName);
      dependentFilterName = DEPENDENT_FILTERS[dependentFilterName];
    }

    const updatedFilters = Object.keys(appliedFilters)?.reduce((curResult, filterKey) => {
      const updatedResult = { ...curResult };

      if (key !== filterKey && !dependentFiltersList?.includes(filterKey)) {
        updatedResult[filterKey] = appliedFilters[filterKey];
      } else if (key === filterKey && !isEmpty(value)) {
        updatedResult[filterKey] = value;
      }

      return updatedResult;
    }, {});

    removeFilterHandler(updatedFilters, key === SEARCH_FIELD_KEY ? null : searchValue);
  };
  return (
    <div className={classes.topBar}>
      {totalResults ? (
        <Typography variant='h5' className={classes.totalResults}>
          {keyEmptyCheck('results', { s: totalResults })}
        </Typography>
      ) : null}
      <Hidden smDown>
        {appliedFiltersList?.map(
          filter =>
            filter?.key !== 'prefix' && (
              <RemovableFiltersTags key={filter?.key} data={filter} onClickHandler={handleRemoveFilter} />
            ),
        )}
      </Hidden>
    </div>
  );
};

FiltersTopBar.propTypes = {
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  totalResults: PropTypes.any.isRequired,
  appliedFilters: PropTypes.object.isRequired,
  dropDowns: PropTypes.object.isRequired,
  searchValue: PropTypes.string.isRequired,
  removeFilterHandler: PropTypes.func.isRequired,
};

FiltersTopBar.defaultProps = {};
export default withStyles(styles)(injectIntl(memo(FiltersTopBar)));
